import { useState, useEffect } from 'react'
import { getOrderHistory } from '../services/api'
import type { OrderHistoryItem } from '../types'

const TARIFF_NAMES: Record<string, string> = {
  econom: 'Эконом',
  comfort: 'Комфорт',
  comfortplus: 'Комфорт+',
  business: 'Бизнес',
  vip: 'VIP',
  ultimate: 'Ultimate',
  maybach: 'Maybach',
  child_tariff: 'Детский',
  lite_b2b: 'Лайт',
  standart_b2b: 'Стандарт',
  optimum_b2b: 'Оптимум',
  premium_van: 'Премиум Ван',
  minivan: 'Минивэн',
}

const STATUS_CONFIG: Record<string, { label: string; color: string; bg: string }> = {
  pending: { label: 'Ожидание', color: 'text-amber-400', bg: 'bg-amber-400/10' },
  created: { label: 'Создан', color: 'text-amber-400', bg: 'bg-amber-400/10' },
  search: { label: 'Поиск машины', color: 'text-blue-400', bg: 'bg-blue-400/10' },
  driving: { label: 'Машина в пути', color: 'text-blue-400', bg: 'bg-blue-400/10' },
  waiting: { label: 'Ожидает', color: 'text-amber-400', bg: 'bg-amber-400/10' },
  transporting: { label: 'В пути', color: 'text-primary', bg: 'bg-primary/10' },
  complete: { label: 'Завершён', color: 'text-accent-green', bg: 'bg-accent-green/10' },
  cancelled: { label: 'Отменён', color: 'text-accent-red', bg: 'bg-accent-red/10' },
  failed: { label: 'Ошибка', color: 'text-accent-red', bg: 'bg-accent-red/10' },
}

interface OrderHistoryProps {
  userId: string
  onBack: () => void
  onOpenOrder: (yandexOrderId: string) => void
}

const formatDate = (iso: string) => {
  const d = new Date(iso)
  const day = d.getDate().toString().padStart(2, '0')
  const month = (d.getMonth() + 1).toString().padStart(2, '0')
  const hours = d.getHours().toString().padStart(2, '0')
  const mins = d.getMinutes().toString().padStart(2, '0')
  return `${day}.${month} ${hours}:${mins}`
}

const shortAddr = (addr: string) => {
  const parts = addr.split(',')
  return parts.slice(0, 2).join(',').trim()
}

const OrderCard = ({
  order,
  onOpenOrder,
}: {
  order: OrderHistoryItem
  onOpenOrder: (id: string) => void
}) => {
  const status = STATUS_CONFIG[order.status] || STATUS_CONFIG.pending
  const isActive = ['pending', 'created', 'search', 'driving', 'waiting', 'transporting'].includes(order.status)

  return (
    <div
      className={`p-3 bg-dark-secondary rounded-2xl border ${isActive ? 'border-primary/20' : 'border-white/5'}`}
    >
      <div className="flex items-center justify-between mb-2">
        <span className="text-text-muted text-xs">{formatDate(order.created_at)}</span>
        <span className={`text-xs px-2 py-0.5 rounded-full ${status.color} ${status.bg}`}>
          {status.label}
        </span>
      </div>

      <div className="flex items-start gap-2 mb-2">
        <div className="flex flex-col items-center pt-1 flex-shrink-0">
          <div className="w-1.5 h-1.5 rounded-full bg-accent-green" />
          <div className="w-0.5 h-4 bg-text-muted/20 my-0.5" />
          <div className="w-1.5 h-1.5 rounded-full bg-primary" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-text-primary text-sm truncate">{shortAddr(order.from_address)}</p>
          <p className="text-text-secondary text-sm truncate mt-1">{shortAddr(order.to_address)}</p>
        </div>
        <div className="text-right flex-shrink-0">
          <p className="text-white text-sm font-semibold">{order.price_rub} ₽</p>
          <p className="text-accent-usdt text-xs">{order.price_usdt.toFixed(2)} USDT</p>
        </div>
      </div>

      <div className="flex items-center justify-between">
        <span className="text-text-muted text-xs">{TARIFF_NAMES[order.tariff] || order.tariff}</span>
        {isActive && order.yandex_order_id && (
          <button
            onClick={() => onOpenOrder(order.yandex_order_id!)}
            className="text-xs text-primary font-medium px-3 py-1 rounded-lg bg-primary/10 hover:bg-primary/20 transition-colors"
          >
            Отслеживать
          </button>
        )}
        {order.status === 'complete' && order.yandex_order_id && (
          <button
            onClick={() => onOpenOrder(order.yandex_order_id!)}
            className="text-xs text-text-secondary font-medium px-3 py-1 rounded-lg bg-white/5 hover:bg-white/10 transition-colors"
          >
            Подробнее
          </button>
        )}
      </div>
    </div>
  )
}

export const OrderHistory = ({ userId, onBack, onOpenOrder }: OrderHistoryProps) => {
  const [active, setActive] = useState<OrderHistoryItem[]>([])
  const [completed, setCompleted] = useState<OrderHistoryItem[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const fetchHistory = async () => {
      setIsLoading(true)
      try {
        const data = await getOrderHistory(userId)
        setActive(data.active)
        setCompleted(data.completed)
      } catch (err: any) {
        console.error('Failed to fetch order history:', err)
        setError('Не удалось загрузить историю заказов')
      } finally {
        setIsLoading(false)
      }
    }
    fetchHistory()
  }, [userId])

  return (
    <div className="h-full flex flex-col bg-dark">
      {/* Header */}
      <div className="flex items-center gap-3 px-4 py-3 border-b border-white/5">
        <button
          onClick={onBack}
          className="w-10 h-10 rounded-xl bg-dark-secondary flex items-center justify-center text-text-secondary hover:text-white transition-colors"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M19 12H5M12 19l-7-7 7-7" />
          </svg>
        </button>
        <h1 className="text-white font-bold text-lg">Мои поездки</h1>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto px-4 py-3 space-y-4">
        {isLoading && (
          <div className="flex items-center justify-center py-20">
            <div className="w-8 h-8 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
          </div>
        )}

        {error && (
          <div className="bg-accent-red/10 rounded-xl px-4 py-3 border border-accent-red/20">
            <p className="text-accent-red text-sm">{error}</p>
          </div>
        )}

        {!isLoading && !error && active.length === 0 && completed.length === 0 && (
          <div className="flex flex-col items-center justify-center py-20">
            <div className="w-20 h-20 rounded-full bg-dark-tertiary flex items-center justify-center mb-4">
              <span className="text-4xl">🚕</span>
            </div>
            <p className="text-text-secondary text-center">Пока нет заказов</p>
            <p className="text-text-muted text-sm text-center mt-1">Закажите такси, и ваши поездки появятся здесь</p>
          </div>
        )}

        {/* Active orders */}
        {active.length > 0 && (
          <div>
            <div className="flex items-center gap-2 mb-2">
              <div className="w-2 h-2 rounded-full bg-accent-green animate-pulse" />
              <h2 className="text-white font-semibold text-sm">Активные заказы</h2>
              <span className="text-text-muted text-xs">({active.length})</span>
            </div>
            <div className="space-y-2">
              {active.map((order) => (
                <OrderCard key={order.id} order={order} onOpenOrder={onOpenOrder} />
              ))}
            </div>
          </div>
        )}

        {/* Completed orders */}
        {completed.length > 0 && (
          <div>
            <h2 className="text-text-secondary font-semibold text-sm mb-2">
              История ({completed.length})
            </h2>
            <div className="space-y-2">
              {completed.map((order) => (
                <OrderCard key={order.id} order={order} onOpenOrder={onOpenOrder} />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
